import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

// Pages that only exist to run an action and then move the user on by
// themselves: the PDF screens render the report, download it and push back to
// the export screen. Stopping on one of them again would restart the whole
// generation, so "back" always steps over them.
const ACTION_ROUTES = ["/report-pdf", "/threat-hunter-report-pdf", "/android-report-pdf"];

const isActionRoute = (pathname) =>
  ACTION_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`));

const entryOf = (location) => ({
  pathname: location.pathname,
  search: location.search || "",
});

const isSameEntry = (a, b) => a.pathname === b.pathname && a.search === b.search;

// Index of the highest entry (below the top one) the given location matches,
// which is where a POP most likely landed.
const findLandingIndex = (stack, entry) => {
  for (let i = stack.length - 2; i >= 0; i--) {
    if (isSameEntry(stack[i], entry)) return i;
  }
  return -1;
};

// Keeps the list of pages visited inside the app during this session and
// resolves where a "back" should actually land.
//
// It is deliberately not history.goBack(): buttons, exports and uploads push
// entries that are not page navigations (the PDF screens push the export
// screen back on top of the stack once the file is downloaded), so a raw back
// would replay those actions instead of returning to the previous page. Only
// real page changes are offered as a back target; everything in between is
// skipped in a single history.go().
const useSmartBack = () => {
  const history = useHistory();

  const [stack, setStack] = useState(() => [entryOf(history.location)]);
  // Where our own history.go() is expected to land, so the POP it triggers
  // trims the stack exactly instead of guessing.
  const pendingIndexRef = useRef(null);

  useEffect(() => {
    const unlisten = history.listen((location, action) => {
      const entry = entryOf(location);

      setStack((prev) => {
        if (action === "PUSH") {
          pendingIndexRef.current = null;
          return [...prev, entry];
        }

        if (action === "REPLACE") {
          pendingIndexRef.current = null;
          return [...prev.slice(0, -1), entry];
        }

        // POP: our own history.go(), the browser back button or a forward move.
        const pendingIndex = pendingIndexRef.current;
        pendingIndexRef.current = null;

        if (pendingIndex !== null && prev[pendingIndex] && isSameEntry(prev[pendingIndex], entry)) {
          return prev.slice(0, pendingIndex + 1);
        }

        const landingIndex = findLandingIndex(prev, entry);

        // No match means the user moved forward again (or past where we started
        // tracking); start over from here so we never offer a back that would
        // leave the application.
        return landingIndex === -1 ? [entry] : prev.slice(0, landingIndex + 1);
      });
    });

    return unlisten;
  }, [history]);

  // The closest previous entry that is a page of its own: not one of the action
  // screens and not the page we are already looking at.
  const backIndex = useMemo(() => {
    const current = stack[stack.length - 1];

    for (let i = stack.length - 2; i >= 0; i--) {
      const entry = stack[i];
      if (isActionRoute(entry.pathname)) continue;
      if (isSameEntry(entry, current)) continue;
      return i;
    }

    return -1;
  }, [stack]);

  const goBack = useCallback(() => {
    if (backIndex === -1) return;

    pendingIndexRef.current = backIndex;
    history.go(backIndex - (stack.length - 1));
  }, [backIndex, history, stack.length]);

  return { canGoBack: backIndex !== -1, goBack };
};

export default useSmartBack;
